import type { ChatModelInfo } from '@/lib/api/providers.client';
import type { ChatSessionDto } from '@/lib/api/sessions.client';
import type { IMessage } from '@/lib/types/chat';
import { ownListsWith } from '@/lib/stores/chat-list-rows';
import { fetchPersisted } from '@/lib/stores/chat-store/chat-fetch';
import { useBudgetStore } from '@/lib/stores/budget.store';
import {
  announceModelFallback,
  effectiveChatModel,
  storedCatalog,
} from '@/lib/stores/chat-store/chat-model';
import { chatFromSession } from '@/lib/stores/chat-store/session-dto';
import {
  activeTabTenantId,
  announceWorkspaceSwitch,
  otherOwnWorkspace,
  reachableChatWorkspace,
  readSessionRow,
  switchTabTenant,
} from '@/lib/stores/chat-store/tenant-access';
import type { GetChatState, SetChatState } from '@/lib/stores/chat.store';
import { sessionToHistory } from '@/lib/stores/session-history';
import { useSessionStore } from '@/lib/stores/session.store';

/**
 * Opening a chat (KAN-1266): read its messages, move the tab into the chat's
 * workspace where that is needed, restore the chat's model and make it the
 * chat on screen.
 */

export interface LoadChatOptions {
  // A load right after a workspace switch: the switch resets every store
  // (model catalog, active chat), so ``loadChat`` restarts from the top in the
  // new workspace, and must not switch again from there. Its own retry after
  // moving the tab, and the chat shell's after someone else's switch dropped
  // the chat mid-load (KAN-1754). A chat that does not live in the workspace
  // the tab is in then stays closed and counts as not found.
  skipTenantSwitch?: boolean;
  signal?: AbortSignal;
}

/** A chat whose messages were read, on its way to the screen. */
interface LoadedChat {
  chatId: string;
  session: ChatSessionDto | null;
  messages: IMessage[];
  // The model the chat restores, and its replacement when it left the catalog.
  model: { model: string | null; swappedTo: string | null };
  // The catalog the model was resolved against.
  catalog: ChatModelInfo[];
}

/**
 * Whether ``chatId`` is still the chat being opened (KAN-1748). Every request
 * of a load can be overtaken: the user opens another chat, or switches the
 * workspace (its reset clears ``activeChatId``), while the request runs. An
 * overtaken load writes nothing more, or the slow answer of a chat the user
 * has left lands on the chat now on screen.
 */
const isOpening = (get: GetChatState, chatId: string): boolean => get().activeChatId === chatId;

/**
 * ``isOpening`` after a workspace switch the load made itself: that switch's
 * reset cleared ``activeChatId``, so the chat is still the one being opened
 * for as long as no other chat has been opened since.
 */
const isOpeningAfterOwnSwitch = (get: GetChatState, chatId: string): boolean => {
  const activeChatId = get().activeChatId;
  return activeChatId === null || activeChatId === chatId;
};

// Whatever the first read failed for: a deleted chat, or one the caller has no
// access to. ``chatLoadError`` lets the UI show "not found" instead of hanging
// on the loading title. Only for the chat still being opened: the late failure
// of a chat the user has left would mark the chat now on screen as not found.
const flagNotFound = (set: SetChatState, get: GetChatState, chatId: string): void => {
  if (isOpening(get, chatId)) set({ chatLoadError: true, currentChat: null });
};

// ``flagNotFound`` after a workspace switch the load made itself. The switch's
// reset may have cleared ``activeChatId``, so the chat is named again.
const flagNotFoundAfterOwnSwitch = (set: SetChatState, get: GetChatState, chatId: string): void => {
  if (isOpeningAfterOwnSwitch(get, chatId)) {
    set({ activeChatId: chatId, chatLoadError: true, currentChat: null });
  }
};

/**
 * The caller's OWN chat can live in another of their workspaces. The session
 * itself loads — its RLS policy admits the owner from anywhere — but its
 * messages are scoped to the chat's workspace and came back empty, so the chat
 * looked wiped while the switcher named the wrong workspace (KAN-1010). The
 * workspace the chat lives in, else ``null``.
 */
const ownChatWorkspace = async (session: ChatSessionDto | null): Promise<string | null> => {
  const profile = useSessionStore.getState().profile;
  const activeTenantId = await activeTabTenantId();
  return otherOwnWorkspace(session, profile?.sub ?? null, activeTenantId);
};

/**
 * Move the tab into the chat's workspace, say so, and load the chat again from
 * the top there, like the agent page does. The way in for the owner's chat
 * (``ownWorkspaceToMoveTo``) and for a chat of the caller's that did not open
 * here at all (``reopenInChatWorkspace``).
 */
const moveToChatWorkspace = async (
  set: SetChatState,
  get: GetChatState,
  chatId: string,
  tenantId: string,
  options?: LoadChatOptions
): Promise<void> => {
  // The user may have left this chat while its metadata loaded. A switch now
  // would move the tab and reset every store under the page they went to.
  if (options?.signal?.aborted) return;
  let switched: boolean;
  try {
    switched = await switchTabTenant(tenantId);
  } catch {
    // No longer a member there: the chat belongs to a workspace the user has
    // left, so it is not theirs to open any more. (A switch can also fail
    // after its reset, which is why this is the after-switch variant.)
    flagNotFoundAfterOwnSwitch(set, get, chatId);
    return;
  }
  if (switched) await announceWorkspaceSwitch();
  if (options?.signal?.aborted || !isOpeningAfterOwnSwitch(get, chatId)) return;
  await get().loadChat(chatId, { skipTenantSwitch: true, signal: options?.signal });
};

/**
 * A chat that did not open in this workspace may still be the caller's: their
 * own, or shared with them, in another of their workspaces (KAN-1754). Ask
 * where it lives and move there the way the owner path does, notice included.
 * Returns whether that took over; ``false`` means "not found".
 *
 * Nothing else is reached by its address: someone else's chat, an agent run of
 * a foreign workspace included, opens only through a share, for super-admins
 * as for anyone. Without the ``skipTenantSwitch`` guard a chat that fails in
 * its OWN workspace would bounce between the two.
 */
const reopenInChatWorkspace = async (
  set: SetChatState,
  get: GetChatState,
  chatId: string,
  options?: LoadChatOptions
): Promise<boolean> => {
  if (options?.skipTenantSwitch) return false;
  const tenantId = await reachableChatWorkspace(chatId);
  const activeTenantId = await activeTabTenantId();
  // Left while locating, or another chat was opened since: neither move the
  // tab nor report this chat as not found.
  if (options?.signal?.aborted || !isOpening(get, chatId)) return true;
  // Not the caller's, or it lives right here and still did not open.
  if (!tenantId || tenantId === activeTenantId) return false;
  await moveToChatWorkspace(set, get, chatId, tenantId, options);
  return true;
};

/**
 * Make the loaded chat the chat on screen.
 *
 * No tenant bookkeeping here. A chat that loaded either lives in the active
 * workspace, or it was reached through a share grant, which is valid from the
 * grantee's own workspace. The two cases that need a different workspace — the
 * owner opening their chat from another of their workspaces, and a chat of the
 * caller's that did not open here at all — switched into it before this point.
 * Writes therefore always target the workspace the switcher names, which is
 * what the chat_messages RLS WITH CHECK demands of a user message
 * (``user_id = NULL`` passes only via ``tenant_id == current_tenant_id``).
 */
const openLoadedChat = (set: SetChatState, get: GetChatState, loaded: LoadedChat): void => {
  const { chatId, session, messages } = loaded;
  const chatModel = loaded.model.model;
  const newChat = chatFromSession(chatId, session, messages, chatModel || get().selectedModel);
  // Opening a chat ALWAYS restores the model it last used, regardless of what
  // was selected in another chat. This keeps the prompt cache warm
  // (continuing a chat with a different model would invalidate it and drive
  // up cost) and matches the user's mental model that each chat owns its
  // model. "Last used" means the newest turn that names a model, not the
  // newest answer — see ``resolveChatModel``. A chat that names none yet
  // leaves the current selection untouched (see the guard below).
  set((state) => ({
    currentChat: newChat,
    ...(chatModel && { selectedModel: chatModel }),
    ...(session && ownListsWith(state, sessionToHistory(session), newChat)),
  }));
};

// Tell the user the chat's model left the catalog and what it continues on.
// Not for imported chats: their model_identifier (e.g. "gpt-4") will never be
// in our catalog, and that is expected.
const announceSwappedModel = async (loaded: LoadedChat): Promise<void> => {
  const { swappedTo } = loaded.model;
  const isImported = loaded.session?.metadata?.imported_from;
  if (!swappedTo || isImported) return;
  const newModelName = loaded.catalog.find((m) => m.id === swappedTo)?.name ?? swappedTo;
  await announceModelFallback(loaded.chatId, newModelName);
};

export const loadChat = async (
  set: SetChatState,
  get: GetChatState,
  chatId: string,
  options?: LoadChatOptions
): Promise<void> => {
  set({ activeChatId: chatId, chatLoadError: false });
  // The catalog has to be there before the check whether the chat's model
  // has left it.
  if (!get().hasLoadedModels) {
    await get().loadModels();
  }
  let messages: IMessage[];
  try {
    messages = await fetchPersisted(chatId);
  } catch {
    // Left meanwhile: neither locate the chat nor report it.
    if (!isOpening(get, chatId)) return;
    if (!(await reopenInChatWorkspace(set, get, chatId, options))) flagNotFound(set, get, chatId);
    return;
  }
  if (!isOpening(get, chatId)) return;

  // The chat's model: the newest turn that names one, or its replacement
  // when that model has left the catalog (``effectiveChatModel``).
  const availableModels = get().availableModels;
  const lowBudget = useBudgetStore.getState().lowBudgetMode;
  const model = effectiveChatModel(messages, storedCatalog(get()), lowBudget);
  const session = await readSessionRow(chatId, get().sessionsLimit);

  const ownWorkspace = await ownChatWorkspace(session);
  // Left while the session row was read: neither move the tab nor open the chat.
  if (!isOpening(get, chatId)) return;
  if (ownWorkspace) {
    if (options?.skipTenantSwitch) {
      // A load that may not move the tab leaves the chat closed rather than
      // open it here without its messages and list it in a workspace it does
      // not belong to (KAN-1754). It says so: stopping silently left the page
      // on its loading title.
      if (!options.signal?.aborted) flagNotFound(set, get, chatId);
      return;
    }
    await moveToChatWorkspace(set, get, chatId, ownWorkspace, options);
    return;
  }
  const loaded: LoadedChat = { chatId, session, messages, model, catalog: availableModels };
  openLoadedChat(set, get, loaded);
  await announceSwappedModel(loaded);
};
